import { useState, useEffect, useContext } from "react";
import {
  Row,
  Col,
  Card,
  Avatar,
  Radio,
  Switch,
  Typography,
  Form,
  Input,
  Button,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import BgProfile from "../assets/images/bg-profile.jpg";
import { useLocation } from "react-router-dom";
import Overview from "./overview";
import Contacts from "./contacts";
import Profileloans from "./profileloans";
import { toast, ToastContainer } from "react-toastify";
import React from "react";
import National from "./national";
import { userContext } from "../context/UserContext";
import Swal from "sweetalert2";
import axios from "axios";

const ChangePasswordCard = ({ email }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const verifyCurrentPassword = async (currentPassword) => {
    try {
      await axios.post("https://cms.paysmart.co.zm/auth/login", {
        email,
        password: currentPassword,
      });
      return true;
    } catch (err) {
      const code = err.response?.data?.errors?.[0]?.extensions?.code;
      return code === "INVALID_OTP";
    }
  };

  const onFinish = async (values) => {
    if (values.newPassword === values.currentPassword) {
      toast.error("New password must be different from the current password", {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }

    setSubmitting(true);

    const verified = await verifyCurrentPassword(values.currentPassword);
    if (!verified) {
      setSubmitting(false);
      toast.error("Current password is incorrect", {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }

    try {
      await axios.patch(
        "https://cms.paysmart.co.zm/users/me",
        { password: values.newPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        },
      );
      form.resetFields();
      Swal.fire({
        title: "Password updated",
        text: "Your password has been changed successfully.",
        icon: "success",
      });
    } catch (err) {
      toast.error(
        err.response?.data?.errors?.[0]?.message || "Failed to update password",
        { position: toast.POSITION.TOP_LEFT },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      title={
        <span>
          <LockOutlined style={{ marginRight: 8 }} />
          Change password
        </span>
      }
      style={{ marginTop: 24 }}
      bodyStyle={{ maxWidth: 480 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Current password"
          name="currentPassword"
          rules={[
            { required: true, message: "Please enter your current password" },
          ]}
        >
          <Input.Password placeholder="Current password" autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          label="New password"
          name="newPassword"
          rules={[
            { required: true, message: "Please enter a new password" },
            { min: 8, message: "Password must be at least 8 characters" },
          ]}
          hasFeedback
        >
          <Input.Password placeholder="New password" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Confirm new password"
          name="confirmPassword"
          dependencies={["newPassword"]}
          hasFeedback
          rules={[
            { required: true, message: "Please confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" autoComplete="new-password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Update password
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

const Profile = () => {
  const location = useLocation();
  const debtor = location.state.debtor;
  const [selectedTab, setSelectedTab] = useState("a"); // Initial tab
  const [stext, setStext] = useState(false);
  const { person, setPerson } = useContext(userContext);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");

  const handleTabChange = (e) => {
    setSelectedTab(e.target.value);
  };

  const showToast = (message, type) => {
    if (type == "success") {
      toast.success(message, {
        position: toast.POSITION.TOP_LEFT,
      });
    } else {
      toast.error(message, {
        position: toast.POSITION.TOP_LEFT,
      });
    }
  };

  const renderTabContent = () => {
    if (selectedTab === "a") {
      return <Overview client={debtor} showToast={showToast} />;
    }
  };

  const onChange = (checked) => {
    setStext(checked);

    let newstatus = "";
    if (checked) {
      newstatus = "active";
    } else {
      newstatus = "archived";
    }
    updateUser(newstatus);
  };

  const updateUser = async (newstatus) => {
    axios
      .patch(
        `https://cms.paysmart.co.zm/users/${debtor.id}`,
        { status: newstatus },

        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        if (person.id == debtor.id) {
          setPerson({ ...person, status: newstatus });
        }

        Swal.fire({
          title: "User status",
          text: "This user's status has been updated",
          icon: "success",
        });
      })
      .catch((error) => {
        // Handle the error
      });
  };

  useEffect(() => {
    if (debtor.status == "active") {
      setStext(true);
    } else {
      setStext(false);
    }
  }, [debtor.status]);

  return (
    <>
      <div
        className="profile-nav-bg"
        style={{ backgroundImage: "url(" + BgProfile + ")" }}
      ></div>

      <Card
        className="card-profile-head"
        bodyStyle={{ display: "none" }}
        title={
          <Row justify="space-between" align="middle" gutter={[24, 0]}>
            <Col span={24} md={12} className="col-info">
              <Avatar.Group>
                <Avatar
                  size={74}
                  shape="square"
                  src={`https://cms.paysmart.co.zm/assets/${debtor.avatar}`}
                />

                <div className="avatar-info">
                  <h4 className="font-semibold m-0">
                    {`${debtor.first_name} ${debtor.last_name}`}
                  </h4>
                  <p>{debtor.email}</p>
                </div>
              </Avatar.Group>
            </Col>
            <Col
              span={24}
              md={9}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Radio.Group value={selectedTab} onChange={handleTabChange}>
                <Radio.Button value="a">
                  {`Balance : k${parseFloat(debtor.balance[0].balance).toFixed(
                    2
                  )}`}
                </Radio.Button>
                {/* <Radio.Button value="b">LOANS</Radio.Button>
                <Radio.Button value="d">NRC/ID</Radio.Button>
                <Radio.Button value="c">USER CONTACTS</Radio.Button> */}
              </Radio.Group>
            </Col>

            {person.role == "aba355e7-ff5b-44f8-97ec-81e06f7b8a95" ? (
              <Col
                span={24}
                md={12}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              ></Col>
            ) : (
              <Col
                span={24}
                md={3}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <p style={{ marginRight: 8 }}>
                  {stext ? "Active" : "Archived"}
                </p>
                <Switch checked={stext} onChange={onChange} />
              </Col>
            )}
          </Row>
        }
      ></Card>
      {renderTabContent()}
      {person.id == debtor.id && <ChangePasswordCard email={person.email} />}
      <ToastContainer />
    </>
  );
};

export default Profile;
